/**
 * A real, running `@modyra/core` form, built from the contract a project compiles to.
 *
 * Actual `field()`/`group()`/`array()`/`createForm()` calls against actual imported validator
 * functions — never a reimplementation, and never text generation, which is a different output for
 * a different purpose.
 *
 * There is deliberately **one** way to get here. Building from the project model as well would give
 * the preview a second, privileged path, and the two would drift: the preview would show forms the
 * export cannot express.
 */
import { array, buildDynamicFormSchema, buildDynamicValidations, createForm, evaluateExpression, field, group, parseDynamicForm, record, serverValidator, } from "@modyra/core";
import { buildIndexes, } from "@modyra/studio-model";
import { compileToContract, toContractExpression } from "@modyra/studio-contract";
import { createMockAsyncValidator } from "./mock-server.js";
/**
 * Every field node in the project, by the path it occupies in the form value.
 *
 * Used to find the nodes the *contract* cannot describe — a server validator is a target-generation
 * concern, so the compiled contract has no place to put one — and reattach them to the form the
 * contract built.
 */
function fieldNodesByPath(node, idx, into) {
    if (node.node === "field") {
        const path = idx.pathByNode.get(node.id);
        if (path !== undefined)
            into.set(path, node);
        return;
    }
    if (node.node === "group") {
        for (const child of node.children)
            fieldNodesByPath(child, idx, into);
        return;
    }
    fieldNodesByPath(node.item, idx, into);
}
/**
 * Puts the preview's mock server validators back onto the schema the contract produced.
 *
 * This is the one thing the preview adds that the contract does not carry, and it is deliberate: a
 * real exported form receives a real server validator from its host, so there is nothing for the
 * schema to hold. Standing in for that host is the preview presenting its own dynamics — a builder
 * affordance — rather than the preview inventing form semantics.
 */
function withMockServerValidators(schema, path, nodesByPath, idx, mockConfigByImplId) {
    const out = {};
    for (const [key, descriptor] of Object.entries(schema)) {
        out[key] = withMockServerValidator(descriptor, path ? `${path}.${key}` : key, nodesByPath, idx, mockConfigByImplId);
    }
    return out;
}
function withMockServerValidator(descriptor, here, nodesByPath, idx, mockConfigByImplId) {
    const shape = descriptor;
    if (shape.kind === "group" && shape.children) {
        return group(withMockServerValidators(shape.children, here, nodesByPath, idx, mockConfigByImplId));
    }
    // A collection's rows are all built from its item descriptor, so the descriptor is where a row's
    // server validator has to go — patching a row would leave every later row without one. The row
    // template has no name of its own: the index files it, and everything under it, at the
    // collection's path, so the item is looked up at `here` rather than below it.
    if (shape.kind === "array" || shape.kind === "record") {
        const item = withMockServerValidator(shape.item, here, nodesByPath, idx, mockConfigByImplId);
        const options = {
            ...(shape.initial !== undefined ? { initial: shape.initial } : {}),
            ...(shape.validators ? { validators: shape.validators } : {}),
        };
        return shape.kind === "array" ? array(item, options) : record(item, options);
    }
    const node = nodesByPath.get(here);
    if (shape.kind !== "field" || !node?.serverValidator)
        return descriptor;
    const sv = node.serverValidator;
    // `skipWhen` is evaluated against the field's *own* value, so a reference to this node reads the
    // whole of it — path `""`. Any other reference is as meaningless here as it was before, and
    // resolves to undefined rather than throwing.
    const selfScoped = new Map(idx.pathByNode);
    selfScoped.set(node.id, "");
    const skipWhen = sv.skipWhen ? toContractExpression(sv.skipWhen, selfScoped) : undefined;
    return field(shape.initial, (shape.validators ?? []), serverValidator(createMockAsyncValidator(mockConfigByImplId[sv.implementationRef] ?? {}), {
        debounceMs: sv.debounceMs,
        timeoutMs: sv.timeoutMs,
        dependsOn: sv.dependencies.map((d) => idx.pathByNode.get(d.nodeId) ?? d.nodeId),
        ...(skipWhen ? { when: (value) => !evaluateExpression(skipWhen, value) } : {}),
    }));
}
/**
 * Builds a real, running form from `project`, **through the contract it would export**.
 *
 * The route is `project → compileToContract → parseDynamicForm → form`, and it is the point of this
 * function rather than an implementation detail. Reading the project model directly — which this did
 * — gave the preview a privileged path: it could build forms the export could not express, so a
 * designer could watch a rule work and then ship a contract without it.
 *
 * The consequence is deliberate: **a project that does not compile does not preview.** Previewing
 * work in progress is a legitimate thing to want, but not at the price of the preview and the export
 * disagreeing about what the form is; the compiler's diagnostics come back with the result so the
 * builder can say precisely what is blocking it.
 */
export function buildLiveForm(project, options = {}) {
    const { mockConfigByImplId = {}, draftStorage, reactivity } = options;
    const { contract, diagnostics } = compileToContract(project);
    if (!contract || !contract.schema)
        return { form: null, diagnostics };
    // The contract compiler already strict-parses what it returns, so this cannot normally fail. It is
    // read rather than assumed because `validations` has to come from somewhere, and taking it from
    // the parser is what keeps the preview's cross-field rules the exported ones.
    const parsed = parseDynamicForm(contract, { mode: "strict" });
    if (!parsed.ok) {
        for (const d of parsed.diagnostics) {
            diagnostics.push({ code: d.code, severity: d.severity, message: d.message, propertyPath: d.path });
        }
        return { form: null, diagnostics };
    }
    const idx = buildIndexes(project);
    const nodesByPath = new Map();
    fieldNodesByPath(project.schema, idx, nodesByPath);
    const schema = withMockServerValidators(buildDynamicFormSchema(contract.schema), "", nodesByPath, idx, mockConfigByImplId);
    const draft = project.behaviors.draft
        ? {
            key: project.behaviors.draft.key,
            exclude: (project.behaviors.draft.exclude ?? []).map((r) => idx.pathByNode.get(r.nodeId) ?? r.nodeId),
            storage: draftStorage,
        }
        : undefined;
    const form = createForm(schema, {
        validators: buildDynamicValidations(parsed.validations),
        draft,
        history: true,
        reactivity,
    });
    return { form, diagnostics };
}
